import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { useCreatePerson } from '@/features/student-management/hooks/useCreatePerson';
import { PersonForm } from '@/features/student-management/ui/organisms/PersonForm';
import { PersonEditTemplate } from '@/features/student-management/ui/templates/PersonEditTemplate';

export const Route = createFileRoute('/_authenticated/persons/create/')({
  component: PersonCreatePage,
});

function extractApiErrorDetail(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const axiosError = error as {
    response?: { data?: { detail?: unknown; title?: unknown } };
  };
  const detail = axiosError.response?.data?.detail;
  const title = axiosError.response?.data?.title;
  if (typeof detail === 'string' && detail.length > 0) return detail;
  if (typeof title === 'string' && title.length > 0) return title;
  return null;
}

function PersonCreatePage() {
  const navigate = useNavigate();
  const { t } = useTranslation('personManagement');
  const createPerson = useCreatePerson();

  const serverError = createPerson.isError
    ? (extractApiErrorDetail(createPerson.error) ?? t('personForm.serverError.generic'))
    : null;

  return (
    <PersonEditTemplate title={t('breadcrumb.create')}>
      <PersonForm
        isSubmitting={createPerson.isPending}
        serverError={serverError}
        onSubmit={data => {
          createPerson.mutate(
            { data },
            {
              onSuccess: () => void navigate({ to: '/persons' }),
            },
          );
        }}
        onCancel={() => void navigate({ to: '/persons' })}
      />
    </PersonEditTemplate>
  );
}
